import { InspectButtonValue } from "@/model/InspectButtonValue";
import { InspectDescriptions } from "@/model/InspectGensha";
import { MD5Inspect } from "@/model/MD5Inspect";
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";

/*
 * inspectLine.vue の WorkAndBuhin 接続、検査結果と整備データの同期、
 * 表示判定を切り出した処理。
 */
export type InspectLineWorkType = "SpeedMeter" | "Diesel";

export interface InspectLineWorkInspectPair {
  speedInspect: InspectDescriptions;
  dieselInspect: InspectDescriptions;
}

export interface InspectLineSeibiSyncState {
  shouldApply: boolean;
  inspectResult?: number;
  workBuhinSelected?: number;
}

export interface InspectLineWorkInspects {
  speedInspect?: InspectDescriptions;
  dieselInspect?: InspectDescriptions;
}

export interface InspectLineConfirmState {
  inspect: InspectDescriptions;
  workBuhinSelected: number;
}

export interface InspectLineValueConfirmState {
  shouldApply: boolean;
  inspectValueList?: any[];
  inspectValue?: any;
  inspectResult?: number;
}

/*
 * showWork を inspectLine.vue から分離し、
 * スピードメーター / ディーゼル黒煙のどちらを WorkAndBuhin へ渡すかを固定するため。
 */
export function resolveInspectLineWorkInspect(
  inspectType: InspectLineWorkType,
  inspects: InspectLineWorkInspectPair
): InspectDescriptions {
  return inspectType === "Diesel"
    ? inspects.dieselInspect
    : inspects.speedInspect;
}

/*
 * showWork を inspectLine.vue から分離し、
 * WorkAndBuhin に渡す shallow copy と showWork=true 付与の契約を固定するため。
 */
export function buildInspectLineWorkBuhinData(inspect: any): any {
  if (!inspect) {
    return {};
  }
  return {
    ...inspect,
    showWork: true
  };
}

/*
 * onConfirmBuhin を inspectLine.vue から分離し、
 * activeInspect から保存対象の元 speedInspect / dieselInspect を選び直す契約を固定するため。
 */
export function resolveInspectLineConfirmInspect(
  activeInspect: any,
  inspects: InspectLineWorkInspectPair
): InspectDescriptions {
  return activeInspect && String(activeInspect.inspectCd) === String(MD5Inspect.Diesel)
    ? inspects.dieselInspect
    : inspects.speedInspect;
}

/*
 * onConfirmBuhin を inspectLine.vue から分離し、
 * 部品確定時に保存対象と workBuhinSelected=Done を返す state を固定するため。
 */
export function buildInspectLineConfirmState(
  activeInspect: any,
  inspects: InspectLineWorkInspectPair
): InspectLineConfirmState {
  return {
    inspect: resolveInspectLineConfirmInspect(activeInspect, inspects),
    workBuhinSelected: STATUS_SELECT_WORK_BUHIN.Done
  };
}

/*
 * setWorkBuhinSelected を inspectLine.vue から分離し、
 * 検査結果と整備選択状態から workBuhinSelected を決める判定を固定するため。
 */
export function resolveInspectLineWorkBuhinSelected(
  inspect: any
): number {
  if (inspect && inspect.inspectResult === InspectButtonValue.CIRCLE) {
    return STATUS_SELECT_WORK_BUHIN.NoNeed;
  }

  const seibiList = inspect && Array.isArray(inspect.seibiList)
    ? inspect.seibiList
    : [];
  const selected = seibiList.find((seibi: any) => seibi && seibi.buhinSelected > 0);
  return selected
    ? STATUS_SELECT_WORK_BUHIN.Done
    : STATUS_SELECT_WORK_BUHIN.Initial;
}

/*
 * saveSeibi 前の同期処理を inspectLine.vue から分離し、
 * inspectLineList の検査結果を整備データへ反映する state を固定するため。
 */
export function buildInspectLineSeibiSyncState(
  inspect: any,
  inspectLineResult: any
): InspectLineSeibiSyncState {
  if (!inspectLineResult || !inspectLineResult.inspectResult) {
    return {
      shouldApply: false
    };
  }

  const nextInspect = {
    ...inspect,
    inspectResult: inspectLineResult.inspectResult
  };
  return {
    shouldApply: true,
    inspectResult: inspectLineResult.inspectResult,
    workBuhinSelected: resolveInspectLineWorkBuhinSelected(nextInspect)
  };
}

/*
 * setInspectData を inspectLine.vue から分離し、
 * API 応答の inspectLine 配下から speedInspect / dieselInspect を探す契約を固定するため。
 */
export function resolveInspectLineWorkInspects(data: any): InspectLineWorkInspects {
  const result: InspectLineWorkInspects = {};
  const inspectLine = data && Array.isArray(data.inspectLine)
    ? data.inspectLine
    : [];
  const subItems =
    inspectLine.length > 0 && Array.isArray(inspectLine[0].subItems)
      ? inspectLine[0].subItems
      : [];

  subItems.forEach((subItem: any) => {
    const inspectDescriptions = subItem && Array.isArray(subItem.inspectDescriptions)
      ? subItem.inspectDescriptions
      : [];
    const speedInspect = inspectDescriptions.find(
      (item: any) => item && String(item.inspectCd) === String(MD5Inspect.SpeedMeter)
    );
    if (speedInspect) {
      result.speedInspect = speedInspect;
    }
    const dieselInspect = inspectDescriptions.find(
      (item: any) => item && String(item.inspectCd) === String(MD5Inspect.Diesel)
    );
    if (dieselInspect) {
      result.dieselInspect = dieselInspect;
    }
  });

  return result;
}

/*
 * onConfirmValue を inspectLine.vue から分離し、
 * スピードメーター値入力 dialog の確定結果を反映する state を固定するため。
 */
export function buildInspectLineValueConfirmState(
  inspectValueList: any[] | null | undefined,
  inspectResult: number | undefined
): InspectLineValueConfirmState {
  if (!inspectValueList || inspectValueList.length === 0) {
    return {
      shouldApply: false
    };
  }

  return {
    shouldApply: true,
    inspectValueList,
    inspectValue: inspectValueList[0].inspectValue,
    inspectResult
  };
}

/*
 * hasSpeedValue を inspectLine.vue から分離し、
 * スピードメーター値入力済みかどうかの template 表示判定を固定するため。
 */
export function hasInspectLineSpeedValue(inspect: any): boolean {
  return !!(
    inspect &&
    inspect.inspectValueList != null &&
    inspect.inspectValueList.length > 0
  );
}

/*
 * hasResult を inspectLine.vue から分離し、
 * 検査ライン結果が入力済みかどうかの template 表示判定を固定するため。
 */
export function hasInspectLineResult(inspectLineResult: any): boolean {
  return !!(inspectLineResult && inspectLineResult.inspectResult > 0);
}

/*
 * hasSpeedMeter を inspectLine.vue から分離し、
 * スピードメーターの検査値、検査結果、部品選択状態の完了判定を固定するため。
 */
export function hasInspectLineSpeedMeterCompleted(
  speedInspect: any,
  speedInspectLineResult: any
): boolean {
  return (
    hasInspectLineSpeedValue(speedInspect) &&
    hasInspectLineResult(speedInspectLineResult) &&
    speedInspect &&
    speedInspect.workBuhinSelected > 0
  );
}

/*
 * hasDieselResult を inspectLine.vue から分離し、
 * ディーゼル黒煙の検査結果と部品選択状態の完了判定を固定するため。
 */
export function hasInspectLineDieselCompleted(
  dieselInspect: any,
  dieselInspectLineResult: any
): boolean {
  return (
    hasInspectLineResult(dieselInspectLineResult) &&
    dieselInspect &&
    dieselInspect.workBuhinSelected > 0
  );
}

/*
 * buttonLabel を inspectLine.vue から分離し、
 * 部品選択ボタンの表示文言を workBuhinSelected から決める契約を固定するため。
 */
export function getInspectLineWorkButtonLabel(inspect: any): string {
  if (inspect && inspect.workBuhinSelected != null && inspect.workBuhinSelected > 0) {
    return "部品選択済";
  }
  return "部品選択";
}
